// src\themes\lino\components\StickyStory.tsx
'use client';

import Image from 'next/image';
import { motion } from 'motion/react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

type StoryItem = {
    title?: string;
    subtitle?: string;
    description?: string;
};

type StickyStoryData = {
    title?: string;
    image?: { url: string };
    thumbnail?: { url: string };
    items?: StoryItem[];
};

export default function StickyStory({ data }: { data: StickyStoryData }) {
    const items = data.items || [];
    const image = data.image || data.thumbnail;
    if (!items.length) return null;

    return (
        <div className="col-span-12 px-4 sm:px-6 lg:px-12">
            {data.title && (
                <motion.h2
                    initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
                    whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    viewport={{ once: false, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="text-xl lg:text-3xl font-light text-primary-900 mb-8 lg:mb-12 tracking-tight"
                >
                    {data.title}
                </motion.h2>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                {image?.url && (
                    <div className="lg:sticky lg:top-28 h-fit">
                        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-primary-100">
                            <Image
                                src={image.url}
                                alt={data.title || ''}
                                fill
                                className="object-cover"
                            />
                        </div>
                    </div>
                )}

                <div className="flex flex-col divide-y divide-primary-200">
                    {items.map((item, index) => (
                        <motion.div
                            key={index}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: false, amount: 0.5 }}
                            variants={textGroupVariants}
                            className="py-7 first:pt-0 flex gap-6"
                        >
                            <motion.span variants={textItemVariants} className="text-primary-300 text-sm font-primary-bold shrink-0 pt-1">
                                {String(index + 1).padStart(2, '0')}
                            </motion.span>
                            <div className="space-y-2.5">
                                {item.title && (
                                    <motion.h3 variants={textItemVariants} className="text-lg text-primary-900 font-primary-bold tracking-tight">
                                        {item.title}
                                    </motion.h3>
                                )}
                                {item.subtitle && (
                                    <motion.p variants={textItemVariants} className="text-[11px] tracking-[0.14em] text-primary-500 uppercase">
                                        {item.subtitle}
                                    </motion.p>
                                )}
                                {item.description && (
                                    <motion.p variants={textItemVariants} className="text-[13px] text-primary-600 leading-7">
                                        {item.description}
                                    </motion.p>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}